
import {
    useEffect, useRef, useState
} from 'react';

export const WebPage = ({html, onLoad}: {html: string, onLoad?: () => void}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    
    const processAndInjectHtml = async () => {
        try {
            if (!containerRef.current) return;
            
            // 如果 html 是一个 URL，先 fetch 内容
            let htmlContent = html;
            if (html.startsWith('http://') || html.startsWith('https://')) {
                const response = await fetch(html);
                htmlContent = await response.text();
            }
            
            // 修改 HTML 内容，确保所有链接在新标签页打开
            const modifiedHtml = htmlContent.replace(
                /<a\s+([^>]*?)>/gi,
                (match, attributes) => {
                    // 如果已经有 target 属性，替换为 _blank
                    if (/target\s*=/i.test(attributes)) {
                        return match.replace(/target\s*=\s*["'][^"']*["']/gi, 'target="_blank"');
                    }
                    // 如果没有 target 属性，添加 target="_blank"
                    return `<a ${attributes} target="_blank">`;
                }
            );
            
            // 创建临时 DOM 来解析 HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(modifiedHtml, 'text/html');
            
            // Add crossOrigin="anonymous" to all images to avoid CORS issues with snapdom/html2canvas
            const images = doc.querySelectorAll('img');
            images.forEach(img => {
                const src = img.getAttribute('src');
                if (src && (src.startsWith('http://') || src.startsWith('https://'))) {
                    img.setAttribute('crossOrigin', 'anonymous');
                }
            });
            
            // 清空容器
            containerRef.current.innerHTML = '';
            
            // 创建 body 容器并注入 body 内容
            const bodyContainer = document.createElement('div');

            
            // 复制 body 的样式属性
            if (doc.body) {
                const bodyStyle = doc.body.getAttribute('style');
                if (bodyStyle) {
                    bodyContainer.setAttribute('style', bodyContainer.getAttribute('style') + '; ' + bodyStyle);
                }
                
                // 复制 body 的类名
                const bodyClass = doc.body.getAttribute('class');
                if (bodyClass) {
                    bodyContainer.className = bodyClass;
                }
                
                // 注入 body 内容
                bodyContainer.innerHTML = doc.body.innerHTML;
            }

            // 动态加载外部资源
            const loadExternalResources = async () => {
                // 检查是否已经加载了 Tailwind CSS
                if (!document.querySelector('script[src="https://cdn.tailwindcss.com"]')) {
                    const tailwindScript = document.createElement('script');
                    tailwindScript.src = 'https://cdn.tailwindcss.com';
                    document.head.appendChild(tailwindScript);
                    
                    // 等待 Tailwind 加载完成
                    await new Promise((resolve) => {
                        tailwindScript.onload = resolve;
                        tailwindScript.onerror = resolve;
                    });
                }
                
                // 检查是否已经加载了 Font Awesome
                if (!document.querySelector('link[href="https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.4.0/css/all.min.css"]')) {
                    const fontAwesomeLink = document.createElement('link');
                    fontAwesomeLink.href = 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.4.0/css/all.min.css';
                    fontAwesomeLink.rel = 'stylesheet';
                    document.head.appendChild(fontAwesomeLink);
                }
                
                // 触发 Tailwind 重新扫描 DOM
                if ((window as any).tailwind) {
                    // 如果 Tailwind 已经加载，手动触发重新扫描
                    setTimeout(() => {
                        if ((window as any).tailwind && (window as any).tailwind.config) {
                            // 强制 Tailwind 重新处理新添加的内容
                            const event = new Event('DOMContentLoaded');
                            document.dispatchEvent(event);
                        }
                    }, 100);
                }
            };
            
            await loadExternalResources();
            
            // 将 body 容器添加到主容器中
            containerRef.current.appendChild(bodyContainer);
            
            setIsLoaded(true);
            
        } catch (error) {
            console.error('Error processing HTML:', error);
            // 如果出错，回退到直接显示 HTML
            if (containerRef.current) {
                containerRef.current.innerHTML = html;
                setIsLoaded(true);
            }
        }
    };

    
    useEffect(() => {
        if(html){
            processAndInjectHtml();
        }
    }, [html]);
    
    // 当内容加载完成时触发回调
    useEffect(() => {
        if (isLoaded && onLoad) {
            onLoad();
        }
    }, [isLoaded, onLoad]);
    
    return (
        <div 
            ref={containerRef}
        />
    )
}